import { DiceStyle, RollSuggetion, TutorialStep } from '../redux/state';
import { toEnumValue } from '../utils/enumUtils';

const SHOW_AVATAR_KEY = 'show_avatar';
const GAME_TAG_KEY = 'game_tag';
const ROLL_SUGGESTIONS_KEY = 'roll_suggestions';
const ORTHOGRAPHIC_CAMERA_KEY = 'enable_orthographic_camera';
const ALTERNTAE_ARROW_KEY = 'alterntae_arrow';
const DICE_STYLE_KEY = 'dice_style';
const TUTORIAL_STEP_KEY = 'tutorial_step';
const TUTORIAL_COMPLETED_KEY = 'tutorial_completed';
const TUTORIAL_DISMISSED_KEY = 'tutorial_dismissed';

// Avatar
const getShowAvatar = (): boolean => {
  const value = localStorage.getItem(SHOW_AVATAR_KEY);
  return value === undefined || value === null || value === true.toString();
};

const setShowAvatar = (show: boolean): void => localStorage.setItem(SHOW_AVATAR_KEY, JSON.stringify(show));

// Game Tag
const getGameTag = (): string | undefined => {
  const tag = localStorage.getItem(GAME_TAG_KEY);
  return tag || undefined;
};

const setGameTag = (tag?: string): void => localStorage.setItem(GAME_TAG_KEY, tag || '');

// Roll Suggestions
const saveRollSuggestions = (rollSuggestions: RollSuggetion[]): void => {
  localStorage.setItem(ROLL_SUGGESTIONS_KEY, JSON.stringify(rollSuggestions));
};

const getRollSuggestions = (): RollSuggetion[] | undefined => {
  const valuesToParse = localStorage.getItem(ROLL_SUGGESTIONS_KEY);

  if (valuesToParse) {
    const rollSuggestions = JSON.parse(valuesToParse) as RollSuggetion[];

    if (Array.isArray(rollSuggestions)) {
      return rollSuggestions;
    }
  }

  return undefined;
};

// Orthographic Camera
const getEnableOrthographicCamera = (): boolean => localStorage.getItem(ORTHOGRAPHIC_CAMERA_KEY) === true.toString();

const setEnableOrthographicCamera = (enable: boolean): void =>
  localStorage.setItem(ORTHOGRAPHIC_CAMERA_KEY, JSON.stringify(enable));

// Alterntae Arrow
const getAlterntaeArrow = (): boolean => localStorage.getItem(ALTERNTAE_ARROW_KEY) === true.toString();

const setAlterntaeArrow = (enable: boolean): void => localStorage.setItem(ALTERNTAE_ARROW_KEY, JSON.stringify(enable));

// Dice Style
const getDiceStyle = (): DiceStyle => {
  const value = localStorage.getItem(DICE_STYLE_KEY);
  const result = toEnumValue(DiceStyle, value ?? '', DiceStyle.Original);
  return result;
};

const setDiceStyle = (diceStyle: DiceStyle): void => {
  localStorage.setItem(DICE_STYLE_KEY, diceStyle);
};

const TutorialSteps: TutorialStep[] = [
  'tap_to_roll',
  'select_strength_modifier',
  'clear_log_with_strength',
  'select_dexterity_modifier',
  'cross_rope_with_dexterity',
  'defeat_tutorial_enemy',
  'completed',
];

const getTutorialStep = (): TutorialStep => {
  const value = localStorage.getItem(TUTORIAL_STEP_KEY);

  switch (value) {
    case 'drag_to_roll':
      return 'select_strength_modifier';
    case 'beat_15_with_two_d20':
      return 'clear_log_with_strength';
    case 'open_dice_panel':
      return 'select_dexterity_modifier';
    case 'add_non_d20':
    case 'add_d6_for_damage':
      return 'cross_rope_with_dexterity';
    case 'mixed_roll_in_two_attempts':
      return 'defeat_tutorial_enemy';
    case 'select_ability_modifier':
    case 'roll_with_ability':
    case 'open_character_panel':
    case 'spend_ability_point':
      return 'completed';
  }

  return value && TutorialSteps.includes(value as TutorialStep) ? (value as TutorialStep) : 'tap_to_roll';
};

const setTutorialStep = (step: TutorialStep): void => {
  localStorage.setItem(TUTORIAL_STEP_KEY, step);
};

const getTutorialCompleted = (): boolean => localStorage.getItem(TUTORIAL_COMPLETED_KEY) === true.toString();

const setTutorialCompleted = (value: boolean): void => {
  localStorage.setItem(TUTORIAL_COMPLETED_KEY, JSON.stringify(value));
};

const getTutorialDismissed = (): boolean => localStorage.getItem(TUTORIAL_DISMISSED_KEY) === true.toString();

const setTutorialDismissed = (value: boolean): void => {
  localStorage.setItem(TUTORIAL_DISMISSED_KEY, JSON.stringify(value));
};

export type SettingsStorage = {
  getShowAvatar: () => boolean;
  setShowAvatar: (show: boolean) => void;
  getGameTag: () => string | undefined;
  setGameTag: (tag?: string) => void;
  saveRollSuggestions: (rollSuggestions: RollSuggetion[]) => void;
  getRollSuggestions: () => RollSuggetion[] | undefined;
  getEnableOrthographicCamera: () => boolean;
  setEnableOrthographicCamera: (enable: boolean) => void;
  getAlterntaeArrow: () => boolean;
  setAlterntaeArrow: (enable: boolean) => void;
  getDiceStyle: () => DiceStyle;
  setDiceStyle: (diceStyle: DiceStyle) => void;
  getTutorialStep: () => TutorialStep;
  setTutorialStep: (step: TutorialStep) => void;
  getTutorialCompleted: () => boolean;
  setTutorialCompleted: (value: boolean) => void;
  getTutorialDismissed: () => boolean;
  setTutorialDismissed: (value: boolean) => void;
};

export const createSettingsStorage = (): SettingsStorage => ({
  getShowAvatar,
  setShowAvatar,
  getGameTag,
  setGameTag,
  saveRollSuggestions,
  getRollSuggestions,
  getEnableOrthographicCamera,
  setEnableOrthographicCamera,
  getAlterntaeArrow,
  setAlterntaeArrow,
  getDiceStyle,
  setDiceStyle,
  getTutorialStep,
  setTutorialStep,
  getTutorialCompleted,
  setTutorialCompleted,
  getTutorialDismissed,
  setTutorialDismissed,
});
